// src/services/billingAdjustments.service.ts
import { db } from '../config/firebase';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { DeterminationChange } from '../types/billing.types';
import { format } from 'date-fns';
import { getLead } from './lead.service';
import { getUserById } from './user.service';

export const billingAdjustmentsService = {
  async getBillingPeriodAdjustments(
    projectId: string,
    periodStart: Date,
    periodEnd: Date
  ): Promise<{ adjustments: DeterminationChange[] }> {
    try {
      const q = query(
        collection(db, 'determinationChanges'),
        where('projectId', '==', projectId),
        where('changedAt', '>=', Timestamp.fromDate(periodStart)),
        where('changedAt', '<=', Timestamp.fromDate(periodEnd))
      );

      const snapshot = await getDocs(q);
      const adjustments = await Promise.all(
        snapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data() as DeterminationChange;
          
          // Ensure we have parent name
          if (!data.parentName && data.leadId) {
            try {
              const lead = await getLead(data.leadId);
              data.parentName = lead?.parentName || 'Unknown';
            } catch (error) {
              console.error('Error fetching lead for parent name:', error);
              data.parentName = 'Unknown';
            }
          }
          
          // Ensure we have changedByName
          if (!data.changedByName && data.changedBy && data.changedBy !== 'system') {
            try {
              const user = await getUserById(data.changedBy);
              data.changedByName = user?.name || data.changedBy;
            } catch (error) {
              console.error('Error fetching user for name:', error);
              data.changedByName = data.changedBy;
            }
          }
          
          return {
            ...data,
            id: docSnapshot.id
          };
        })
      );

      return { adjustments };
    } catch (error) {
      console.error('Error getting billing period adjustments:', error);
      return { adjustments: [] };
    }
  },

  async getBillingWeekAdjustments(
    projectId: string,
    weekStart: Date
  ): Promise<{ adjustments: DeterminationChange[] }> {
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    
    try {
      const q = query(
        collection(db, 'determinationChanges'),
        where('projectId', '==', projectId),
        where('weekOfChange', '==', weekStartStr)
      );

      const snapshot = await getDocs(q);
      const adjustments = await Promise.all(
        snapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data() as DeterminationChange;
          
          // Enhance with missing data
          if (!data.parentName && data.leadId) {
            const lead = await getLead(data.leadId);
            data.parentName = lead?.parentName || 'Unknown';
          }
          
          if (!data.changedByName && data.changedBy && data.changedBy !== 'system') {
            const user = await getUserById(data.changedBy);
            data.changedByName = user?.name || data.changedBy;
          }
          
          return {
            ...data,
            id: docSnapshot.id
          };
        })
      );

      return { adjustments };
    } catch (error) {
      console.error('Error getting week adjustments:', error);
      return { adjustments: [] };
    }
  },

  async getPendingAdjustments(
    projectId: string,
    lastInvoiceDate?: Date
  ): Promise<DeterminationChange[]> {
    try {
      let q;
      if (lastInvoiceDate) {
        q = query(
          collection(db, 'determinationChanges'),
          where('projectId', '==', projectId),
          where('billed', '==', false),
          where('changedAt', '>', Timestamp.fromDate(lastInvoiceDate))
        );
      } else {
        q = query(
          collection(db, 'determinationChanges'),
          where('projectId', '==', projectId),
          where('billed', '==', false)
        );
      }

      const snapshot = await getDocs(q);
      const adjustments = await Promise.all(
        snapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data() as DeterminationChange;
          
          // Enhance with missing data
          if (!data.parentName && data.leadId) {
            const lead = await getLead(data.leadId);
            data.parentName = lead?.parentName || 'Unknown';
          }
          
          if (!data.changedByName && data.changedBy) {
            const user = await getUserById(data.changedBy);
            data.changedByName = user?.name || data.changedBy;
          }
          
          return {
            ...data,
            id: docSnapshot.id
          };
        })
      );
      
      return adjustments;
    } catch (error) {
      console.error('Error getting pending adjustments:', error);
      return [];
    }
  },

  async createDeterminationChange(
    change: Omit<DeterminationChange, 'id' | 'changedAt'>
  ): Promise<void> {
    try {
      // Fetch parent name if not provided
      let parentName = change.parentName;
      if (!parentName && change.leadId) {
        try {
          const lead = await getLead(change.leadId);
          parentName = lead?.parentName || 'Unknown';
        } catch (error) {
          console.error('Error fetching lead:', error);
          parentName = 'Unknown';
        }
      }

      // Fetch user name if not provided
      let changedByName = change.changedByName;
      if (!changedByName && change.changedBy && change.changedBy !== 'system') {
        try {
          const user = await getUserById(change.changedBy);
          changedByName = user?.name || change.changedBy;
        } catch (error) {
          console.error('Error fetching user:', error);
          changedByName = change.changedBy;
        }
      }

      await addDoc(collection(db, 'determinationChanges'), {
        ...change,
        parentName,
        changedByName: changedByName || 'System',
        billed: false,
        changedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error creating determination change:', error);
      throw error;
    }
  },

  async markAdjustmentsAsBilled(adjustmentIds: string[]): Promise<void> {
    try {
      const batch = [];
      for (const id of adjustmentIds) {
        batch.push(
          updateDoc(doc(db, 'determinationChanges', id), {
            billed: true,
            billedAt: Timestamp.now()
          })
        );
      }
      await Promise.all(batch);
    } catch (error) {
      console.error('Error marking adjustments as billed:', error);
      throw error;
    }
  },

  formatAdjustmentsForExport(adjustments: DeterminationChange[]): any[] {
    return adjustments.map(adj => ({
      'Lead ID': adj.leadId,
      'Parent Name': adj.parentName || 'Unknown',
      'Previous': adj.previousDetermination,
      'New': adj.newDetermination,
      'Changed Date': adj.changedAt ? format(adj.changedAt.toDate(), 'MM/dd/yyyy HH:mm') : '',
      'Changed By': adj.changedByName || adj.changedBy || 'System',
      'Notes': adj.notes || '',
      'Impact': adj.newDetermination === 'valid' ? '+1' : adj.previousDetermination === 'valid' ? '-1' : '0',
      'Billed': adj.billed ? 'Yes' : 'No'
    }));
  }
};